import React, {
  ComponentType, memo,
  useCallback,
  useEffect,
  useMemo, useRef,
  useState,
} from 'react';
import {
  IconButton,
  InputBase,
  Stack,
} from '@mui/material';
import Box from '@mui/material/Box';
import { datagridColors } from '../../assets/datagridStyles';
import {
  DataGridPro,
  GridColDef,
  GridFilterModel,
  GridFooter,
  GridPaginationModel,
  GridSortModel,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid-pro';
import { Header } from '../AdminPages/Header';
import { useGridApiRef} from '@mui/x-data-grid';
import {styled, useTheme} from '@mui/material/styles';
import _, { debounce } from 'lodash';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import api from '../../services/api';
import {
  FilterAltOutlined,
  FilterList
} from "@mui/icons-material";
import useMediaQuery from "@mui/material/useMediaQuery";
const renderData = (data:any, depth = 0, mr:boolean = false) => {
  const x = mr ? 0 : 20;

  const indentStyle = {
    marginLeft: `${depth * 20}px`, // A mélység alapján növeljük a margót
  };

  if (typeof data === "object" && data !== null) {
    if (Array.isArray(data)) {
      // Ha tömb, listázd az elemeket
      return (
        <ul style={indentStyle}>
          {data.map((item, index) => (
            <li key={index}>{renderData(item, depth + 1)}</li>
          ))}
        </ul>
      );
    } else {
      // Ha objektum, jelenítsd meg a kulcs-érték párokat
      return (
        <ul style={indentStyle}>
          {Object.entries(data).map(([key, value]) => (
            <li key={key}>
              <span><b>{key}:</b> <span>{renderData(value, depth + 1)}</span></span>
            </li>
          ))}
        </ul>
      );
    }
  }
  // Ha primitív érték, egyszerűen jelenítsd meg
  return <span>{data !== null ? data.toString() : "null"}</span>;
};
export const LogPage: ComponentType = () => {
  const themeQuery = useTheme();
  const isMobile = useMediaQuery(themeQuery.breakpoints.down("lg"));
  const [loading, setLoading] = useState(false);
  const [addNewEntity, setAddNewEntity] = useState(false);
  const [rows, setRows] = useState([]);
  const apiRef = useGridApiRef();
  const [fastSearchValue, setFastSearchValue] = useState<any>('');
  const [rowCount, setRowCount] = useState<any>(0);
  const [dateFilter, setDateFilter] = useState<any>(['', '']);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 50,
    page: 0,
  });
  const [openHeaderFilters, setOpenHeaderFilters] = useState(false);
  const [openFilters, setOpenFilters] = useState(!isMobile);

  const [filterModelStatic, setFilterModelStatic] = useState<GridFilterModel>({items: []});
  const [sortModelStatic, setSortModelStatic] = useState<GridSortModel>([]);
  const [paginationModelStatic, setPaginationModelStatic] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 50,
  });
  const [userFilterOptions, setUserFilterOptions] = useState<any>([]);
  const [filters, setFilters] = useState({});
  const autosizeOptions = {
    includeHeaders: true,
    includeOutliers: true,
    outliersFactor: 1.5,
    expand: true,
  };
  const debouncedOnChangeSearch = debounce(handleDebouncedOnChangeSearch, 500);

  function handleDebouncedOnChangeSearch(value: any) {
    setFastSearchValue(value);
  }

  const fetchLogs = useCallback(() => {
    setLoading(true);

    const searchParams = new URLSearchParams();
    searchParams.set('page', (paginationModelStatic.page + 1).toString());
    searchParams.set('pageSize', paginationModelStatic.pageSize.toString());
    searchParams.set('from', dateFilter[0] || '');
    searchParams.set('to', dateFilter[1] || '');
    searchParams.set('filters', JSON.stringify(filterModelStatic));
    searchParams.set('sortModel', JSON.stringify(sortModelStatic));

    api.get(`/logs`, searchParams).then((res: any) => {
      setRows(res.data.data);
      setRowCount(res.data.total);

      setUserFilterOptions(res.filter_options.users);
    })
      .then(() => sleep(0))
      .then(() => {
        if (apiRef?.current) {
          apiRef.current.autosizeColumns(autosizeOptions);
        }
      })
      .finally(()=>{
      setLoading(false);
    });
  }, [paginationModelStatic, dateFilter, filters,fastSearchValue,filterModelStatic]);

  const debouncedSetPaginationModel = _.debounce(setPaginationModel, 300);

  useEffect(() => {
    fetchLogs();
  }, [paginationModelStatic, dateFilter, filters,fastSearchValue,filterModelStatic]);

  const handleChangeHeaderFilter = (column: string, value: any) => {
    setFilters((prevFilters) => {
      const updatedFilters:any = { ...prevFilters };
      if (!value) {
        delete updatedFilters[column];
      } else {
        updatedFilters[column] = value;
      }
      return updatedFilters;
    });
  };

  const columns: GridColDef[] = useMemo(() => {
    const columnArray: GridColDef[] = [
      {
        field: 'actions',
        type: 'actions',
        headerName: '',
        cellClassName: 'actions',
        maxWidth: 50,
        getActions: (props: any) => {
          return [<>{props?.row?.row_number}</>];
        },
        renderHeader: (props: any) => {
          return (
            <IconButton
              size={'small'}
              onClick={() => {
                setOpenHeaderFilters(!openHeaderFilters);
              }}
            >
              <FilterAltOutlined
                sx={{
                  color: '#fff',
                }}
              />
            </IconButton>
          );
        },
      },
      {
        field: 'created_at',
        headerName: 'Created',
        editable: false,
        type: 'dateTime',
        valueGetter: (params) => params.value ? new Date(params.value) : null, // ⬅️ Biztosítja, hogy `Date` objektum legyen
      },
      {
        field: 'user',
        headerName: 'User',
        editable: false,
        type: 'string',
        renderCell: (params: any) => {
          return <div>{params?.value?.name}</div>;
        },
      },
      {
        field: 'action',
        headerName: 'Action',
        editable: false,
        type: 'string',
      },

      {
        field: 'db_table',
        headerName: 'Database',
        editable: false,
        type: 'string',
      },
      {
        field: 'model_id',
        headerName: 'ID',
        editable: false,
        type: 'string',
      },
      {
        field: 'name',
        headerName: 'Name',
        editable: false,
        type: 'string',
        renderCell: (params:any) =>{
          const value:any = params?.value;
          return renderData(value);
        },
      },
      {
        field: 'changes',
        headerName: 'Changes',
        editable: false,
        type: 'string',
        renderCell: (params:any) =>{
          const value:any = params?.value;
          return renderData(value);
        },
      },
    ];
    return columnArray;
  }, []);

  const CustomFooter = () => {
    return (
      <Stack
        direction={'row'}
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 8px',
        }}
      >
        <IconButton
          onClick={() => {
            setAddNewEntity(true);
          }}
        >
          <AddIcon color={'primary'} />
        </IconButton>
        <GridFooter />
      </Stack>
    );
  };
  function CustomSortIconDesc(props: any) {
    return (
      <div
        style={{
          background: '#42948c',
          borderRadius: '40px',
          height: '15px',
          width: '15px',
        }}
      >
        <ArrowDropDownRoundedIcon
          color={'secondary'}
          sx={{
            width: '15px',
            height: '15px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>
    );
  }

  function CustomSortIconAsc(props: any) {
    return (
      <div
        style={{
          background: '#42948c',
          borderRadius: '40px',
          height: '15px',
          width: '15px',
        }}
      >
        <ArrowDropUpRoundedIcon
          color={'secondary'}
          sx={{
            width: '15px',
            height: '15px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>
    );
  }

  function CustomSortIcon(props: any) {
    return (
      <div
        style={{
          background: '#42948c',
          borderRadius: '40px',
          height: '15px',
          width: '15px',
        }}
      >
        <ArrowDropUpRoundedIcon
          color={'secondary'}
          sx={{
            width: '15px',
            height: '15px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>
    );
  }

  const quickFilterRef = useRef<HTMLInputElement | null>(null);

  const CustomToolbar = memo(() => {
    return (
      <GridToolbarContainer
        sx={{
          gap: isMobile ? 0 : 1,
          display: openFilters ? "flex" : "none",
        }}
      >

        {!isMobile && (
          <GridToolbarFilterButton/>
        )}

        <Box sx={{flexGrow: 1}}/>

        <GridToolbarQuickFilter
          sx={{width: isMobile ? "120px" : '200px'}}
          debounceMs={400}
          inputRef={(el) => {
            if (el && !quickFilterRef.current) {
              quickFilterRef.current = el; // Megőrzi a ref-et, nem írja felül minden rendernél
            }
          }}
        />

      </GridToolbarContainer>
    );
  });

  const handleFilterModelChange = useCallback((newModel:any) => {
    setFilterModelStatic((prevModel) =>
      JSON.stringify(prevModel) === JSON.stringify(newModel) ? prevModel : newModel
    );
  }, []);

  const handleSortModelChange = useCallback((newModel:any) => {
    setSortModelStatic((prevModel) =>
      JSON.stringify(prevModel) === JSON.stringify(newModel) ? prevModel : newModel
    );
  }, []);

  const handlePaginationModelChange = useCallback((newModel:any) => {
    setPaginationModelStatic((prevModel) =>
      JSON.stringify(prevModel) === JSON.stringify(newModel) ? prevModel : newModel
    );
  }, []);

  const MemoizedCustomToolbar = useMemo(() => CustomToolbar, [
    openFilters
  ]);

  return (
    <>
      <Header
        title={'Logs'}
        hasDateFilter={true}
        onChangeDateFilter={(from, to) => {
          setDateFilter([from, to]);
        }}
        rowCount={rows.length}
        hasPagination={false}
      />
      <Box sx={datagridColors('calc(100vh - 145px)')}>
        <DataGridPro
          unstable_headerFilters={openHeaderFilters}
          apiRef={apiRef}
          sx={{
            pointerEvents: loading ? 'none' : 'auto',
            opacity: loading ? 0.5 : 1,
          }}
          rows={rows}
          getRowClassName={(params: any) => {
            const color =
              params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
            return `${color}`;
          }}
          disableColumnMenu={true}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          columnHeaderHeight={38}
          getRowHeight={(row)=> 'auto'}
          hasPagination={true}
          pagination={true}
          paginationMode={'server'}
          filterMode={'server'}
          sortingMode={'server'}
          slots={{
            // @ts-ignore
            columnSortedDescendingIcon: CustomSortIconDesc,
            columnSortedAscendingIcon: CustomSortIconAsc,
            columnUnsortedIcon: CustomSortIcon,
            filterIcon: FilterList,
            headerFilterMenu: null,
            toolbar: MemoizedCustomToolbar,
          }}
          pageSizeOptions={[25, 50, 100]}
          rowCount={rowCount}

          paginationModel={paginationModelStatic}
          filterModel={filterModelStatic}
          sortModel={sortModelStatic}

          onFilterModelChange={handleFilterModelChange}
          onSortModelChange={handleSortModelChange}
          onPaginationModelChange={handlePaginationModelChange}

        />
      </Box>
    </>
  );
};
function sleep(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
